import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState
} from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { darkTheme, defaultTheme } from '../../config';

export const ThemeContext = createContext<{
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  hasDarkTheme: boolean;
}>({
  theme: 'light',
  hasDarkTheme: darkTheme !== null,
  toggleTheme: (): void => {
    /* Do nothing */
  }
});

export function ThemeProvider({
  children
}: PropsWithChildren<unknown>): JSX.Element {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    if (
      typeof window?.localStorage?.getItem === 'function' &&
      window.localStorage.getItem('prefer-theme') !== null &&
      ['light', 'dark'].includes(
        window.localStorage.getItem('prefer-theme') as string
      )
    ) {
      setTheme(window.localStorage.getItem('prefer-theme') as 'light' | 'dark');
    }
  }, []);

  const toggleAndPersistTheme = useCallback(() => {
    setTheme((t) => {
      const newTheme = t === 'light' ? 'dark' : 'light';
      if (typeof window?.localStorage?.setItem === 'function') {
        window.localStorage.setItem('prefer-theme', newTheme);
      }

      return newTheme;
    });
  }, []);

  return (
    <StyledThemeProvider
      theme={theme === 'dark' && darkTheme !== null ? darkTheme : defaultTheme}
    >
      <ThemeContext.Provider
        value={{
          theme,
          hasDarkTheme: darkTheme !== null,
          toggleTheme: toggleAndPersistTheme
        }}
      >
        {children}
      </ThemeContext.Provider>
    </StyledThemeProvider>
  );
}
